import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Bell, Users, Globe, AlertCircle, Wifi, FileText, CreditCard, ShieldCheck, Mail } from 'lucide-react';
import './Terms.css';

// App-specific data
const appData = {
    pdfhub: {
        name: "PDF Hub",
        icon: "📑",
        description: "PDF management application",
        dataCollection: [
            "No personal documents are collected or stored",
            "PDF processing happens entirely on your device",
            "We cannot access the content of your files"
        ],
        permissions: ["Storage access to read and save PDF files"]
    },
    pdfgen: {
        name: "PDF Gen",
        icon: "📄",
        description: "PDF creation and conversion application",
        isDetailed: true
    },
    smartzip: {
        name: "SmartZip",
        icon: "📦",
        description: "File compression and extraction application",
        dataCollection: [
            "No files are uploaded or stored externally",
            "Compression happens entirely on your device",
            "We cannot see your file contents"
        ],
        permissions: ["Storage access to read and write files"]
    },
    focushub: {
        name: "FocusHub",
        icon: "🎯",
        description: "Focus timer and productivity application",
        dataCollection: [
            "Focus session data is stored only on your device",
            "We do not collect or share productivity statistics",
            "No account or login required"
        ],
        permissions: ["Notification access for timer alerts"]
    },
    neartransfer: {
        name: "NearTransfer",
        icon: "📲",
        description: "cross-platform file sharing application that enables seamless file transfers between Android and Windows devices over the same WiFi network",
        isDetailed: true
    },
    novacraft: {
        name: "NovaCraft Studio",
        icon: "🎬",
        description: "Stop motion animation application",
        dataCollection: [
            "Animations are stored only on your device",
            "We do not access your creative projects",
            "No cloud sync or upload features"
        ],
        permissions: ["Camera access for capturing frames", "Storage access to save animations"]
    },
    geocam: {
        name: "Geocam Pro",
        icon: "📸",
        description: "Professional geo-tagged camera application",
        isDetailed: true
    },
    atsai: {
        name: "Ats.Ai",
        icon: "🤖",
        description: "AI-powered resume builder and ATS score checker",
        isDetailed: true
    },
    ourverse: {
        name: "Ourverse",
        icon: "🌌",
        description: "Real-time synchronization space for partners with shared doodles, mini-games, voice calling, and synced music.",
        isDetailed: true
    }
};

// Detailed NearTransfer Privacy Policy Component
const NearTransferPrivacy = ({ navigate }) => {
    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>📲 NearTransfer - Privacy Policy</h1>
                    <p className="last-updated">Last Updated: January 2026</p>
                </div>
            </div>

            <div className="terms-content">
                <section className="terms-section">
                    <div className="section-icon">
                        <Shield size={24} />
                    </div>
                    <h2>Introduction</h2>
                    <p>
                        NearTransfer ("we", "our", or "the App") is a peer-to-peer file sharing application that allows users to transfer files, contacts, and other data between devices connected to the same local WiFi network. The App is available on the Google Play Store (Android) and the Microsoft Store (Windows). This Privacy Policy explains how we handle your data.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Database size={24} />
                    </div>
                    <h2>Data Collection and Usage</h2>

                    <h3>What We DON'T Collect</h3>
                    <div className="highlight-box">
                        <ul>
                            <li><strong>Your Files:</strong> We never upload, store, or access your files on any external servers. All file transfers occur directly between your devices over your local network.</li>
                            <li><strong>Personal Information:</strong> We do not collect names, email addresses, phone numbers, or any personally identifiable information.</li>
                            <li><strong>Transfer Content:</strong> All transferred data is sent directly to your other device and is never stored on our servers.</li>
                        </ul>
                    </div>

                    <h3>What We May Collect</h3>
                    <ul>
                        <li><strong>Anonymous Analytics:</strong> Basic app usage statistics (feature usage, crash reports) to improve the app experience. This can be disabled in Settings.</li>
                        <li><strong>Advertising Data:</strong> On mobile platforms (Android/iOS), our ad partner (Google AdMob) may collect advertising identifiers for personalized ads. See Google's Privacy Policy for details. On Windows, the App does not display advertisements.</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Lock size={24} />
                    </div>
                    <h2>Permissions We Request</h2>

                    <h3>Required Permissions</h3>
                    <div className="permissions-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Permission</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Internet</td>
                                    <td>For signaling server connection to establish peer-to-peer transfers</td>
                                </tr>
                                <tr>
                                    <td>WiFi/Network State</td>
                                    <td>To discover nearby devices on your local network</td>
                                </tr>
                                <tr>
                                    <td>Storage</td>
                                    <td>To read files you want to share and save received files</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>Optional Permissions</h3>
                    <div className="permissions-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Permission</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Camera</td>
                                    <td>QR code scanning for quick device pairing</td>
                                </tr>
                                <tr>
                                    <td>Contacts</td>
                                    <td>Contact sharing feature - export/import contacts</td>
                                </tr>
                                <tr>
                                    <td>Photos & Videos</td>
                                    <td>Media gallery access for sharing photos and videos</td>
                                </tr>
                                <tr>
                                    <td>Installed Apps</td>
                                    <td>To list and share installed applications as APK files</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Wifi size={24} />
                    </div>
                    <h2>Data Transfer</h2>
                    <p>All data transfers in NearTransfer occur:</p>
                    <ul>
                        <li><strong>Peer-to-Peer:</strong> Directly between your devices</li>
                        <li><strong>Local Network Only:</strong> Over your WiFi network, not through the internet</li>
                        <li><strong>Encrypted:</strong> Using secure TCP socket connections</li>
                        <li><strong>No Intermediary:</strong> Without passing through our servers</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Globe size={24} />
                    </div>
                    <h2>Third-Party Services</h2>

                    <h3>Google AdMob</h3>
                    <p>We display advertisements through Google AdMob. Google may collect:</p>
                    <ul>
                        <li>Advertising ID</li>
                        <li>Device information</li>
                        <li>General location (country/region)</li>
                    </ul>
                    <p>You can opt out of personalized ads in your device settings. For more information, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</p>

                    <h3>In-App Purchases</h3>
                    <p>We use platform-native billing for premium subscriptions: Google Play Billing on Android and Microsoft Store on Windows. Purchase data is handled by the respective platform according to their privacy policies.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <ShieldCheck size={24} />
                    </div>
                    <h2>Data Security</h2>
                    <ul>
                        <li>All file transfers use <strong>encrypted TCP connections</strong></li>
                        <li>We do not store any user data on external servers</li>
                        <li>File transfers happen directly between devices without intermediaries</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Users size={24} />
                    </div>
                    <h2>Children's Privacy</h2>
                    <p>
                        NearTransfer is not intended for children under 13. We do not knowingly collect information from children under 13.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Bell size={24} />
                    </div>
                    <h2>Your Rights</h2>
                    <p>You have the right to:</p>
                    <ul>
                        <li><strong>Deny Permissions:</strong> Refuse any optional permission without losing core functionality</li>
                        <li><strong>Disable Analytics:</strong> Turn off anonymous usage statistics in Settings</li>
                        <li><strong>Opt-Out of Personalized Ads:</strong> Through your device's advertising settings</li>
                        <li><strong>Delete Data:</strong> Uninstalling the app removes all locally stored app data</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <FileText size={24} />
                    </div>
                    <h2>Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last Updated" date.
                    </p>
                </section>

                <section className="terms-section contact-section">
                    <div className="section-icon">
                        <FileText size={24} />
                    </div>
                    <h2>Contact Us</h2>
                    <p>If you have questions about this Privacy Policy, please contact us at:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> agnishanth609@gmail.com</p>
                    </div>
                    <p>
                        <strong>NearTransfer</strong><br />
                        A peer-to-peer file sharing app<br />
                        © 2026 NearTransfer. All rights reserved.
                    </p>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2026 NearTransfer. All rights reserved.</p>
            </div>
        </div>
    );
};

// Detailed PDFGen Privacy Policy Component
const PDFGenPrivacy = ({ navigate }) => {
    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>📄 PDFGen - Privacy Policy</h1>
                    <p className="last-updated">Last Updated: January 7, 2026</p>
                </div>
            </div>

            <div className="terms-content">
                <section className="terms-section">
                    <div className="section-icon">
                        <Shield size={24} />
                    </div>
                    <h2>Introduction</h2>
                    <p>
                        PDFGen ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile application PDFGen (the "App").
                    </p>
                    <p>This app is developed and published by the PDFGen app developer.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Database size={24} />
                    </div>
                    <h2>Information We Collect</h2>

                    <h3>Information You Provide</h3>
                    <ul>
                        <li><strong>PDF Files:</strong> Documents you create, edit, or manage using our app are stored locally on your device only.</li>
                        <li><strong>Images:</strong> Photos you capture or select for PDF conversion are processed locally.</li>
                        <li><strong>Contacts:</strong> When using Business Card Scanner, contact information is extracted via OCR and saved to your device contacts only with your permission.</li>
                        <li><strong>Digital Signatures:</strong> Signature data you create is stored locally on your device.</li>
                    </ul>

                    <h3>Automatically Collected Information</h3>
                    <ul>
                        <li><strong>Device Information:</strong> Basic device type and operating system version for compatibility purposes.</li>
                        <li><strong>Crash Reports:</strong> Anonymous crash data to improve app stability.</li>
                        <li><strong>Ad Data:</strong> We use Google AdMob to display advertisements. AdMob may collect device identifiers and usage data as described in <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
                    </ul>

                    <div className="highlight-box">
                        <h3>Information We Do NOT Collect</h3>
                        <ul>
                            <li>We do NOT upload your PDF files to any server</li>
                            <li>We do NOT collect personal identification information</li>
                            <li>We do NOT track your location</li>
                            <li>We do NOT access your files without your explicit action</li>
                            <li>We do NOT share your documents with third parties</li>
                        </ul>
                    </div>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Eye size={24} />
                    </div>
                    <h2>How We Use Your Information</h2>
                    <ul>
                        <li>To provide PDF conversion, scanning, and document management features</li>
                        <li>To save contacts from scanned business cards (with your permission)</li>
                        <li>To display advertisements via Google AdMob, which may be personalized or non-personalized based on user consent and region</li>
                        <li>To improve app performance and fix bugs</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Lock size={24} />
                    </div>
                    <h2>Permissions We Request</h2>
                    <div className="permissions-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Permission</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Camera</td>
                                    <td>Document scanning and photo capture</td>
                                </tr>
                                <tr>
                                    <td>Storage/Photos</td>
                                    <td>Import images and save PDF files</td>
                                </tr>
                                <tr>
                                    <td>Contacts</td>
                                    <td>Save scanned business card contacts (optional)</td>
                                </tr>
                                <tr>
                                    <td>Internet</td>
                                    <td>Display advertisements</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <ShieldCheck size={24} />
                    </div>
                    <h2>Data Storage and Security</h2>
                    <p>
                        All PDF files, images, and documents are stored <strong>locally on your device only</strong>. We do not upload or store your files on any external servers. Your data remains under your control at all times.
                    </p>
                    <p>
                        We implement appropriate security measures including biometric authentication option to protect your documents.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Globe size={24} />
                    </div>
                    <h2>Third-Party Services</h2>
                    <p>Our app uses the following third-party services:</p>
                    <ul>
                        <li><strong>Google AdMob:</strong> For displaying advertisements. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
                        <li><strong>Google ML Kit:</strong> For OCR text recognition and document scanning (processed on-device)</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Users size={24} />
                    </div>
                    <h2>Children's Privacy</h2>
                    <p>
                        Our app is not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>Your Rights</h2>
                    <ul>
                        <li>You can delete any PDF or document created by the app at any time</li>
                        <li>You can revoke permissions through your device settings</li>
                        <li>You can uninstall the app to remove all locally stored data</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <h2>Regional Privacy Rights</h2>
                    <p>
                        Depending on your location (such as the European Economic Area), you may have additional rights under data protection laws, including the right to access, correct, or delete your data. Since PDFGen does not store personal data on external servers, most data can be managed directly on your device.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Bell size={24} />
                    </div>
                    <h2>Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last Updated" date at the top of this policy.
                    </p>
                </section>

                <section className="terms-section contact-section">
                    <div className="section-icon">
                        <FileText size={24} />
                    </div>
                    <h2>Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> pdfgen09@gmail.com</p>
                        <p><strong>Developer:</strong> PDFGen</p>
                    </div>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2026 PDFGen. All rights reserved.</p>
            </div>
        </div>
    );
};

// Detailed Geocam Privacy Policy Component
const GeocamPrivacy = ({ navigate }) => {
    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>📸 GEOCAM PRO - Privacy Policy</h1>
                    <p className="last-updated">Effective Date: January 31, 2026 | Last Updated: January 31, 2026</p>
                </div>
            </div>

            <div className="terms-content">
                <section className="terms-section">
                    <div className="section-icon">
                        <Shield size={24} />
                    </div>
                    <h2>Introduction</h2>
                    <p>
                        Welcome to GEOCAM PRO ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we handle your data. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your personal data.
                    </p>
                    <p>
                        By using GEOCAM PRO, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Database size={24} />
                    </div>
                    <h2>1. Information We Collect</h2>

                    <h3>1.1 Location Data</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> GPS coordinates, altitude, heading, and speed from your device's location sensors.</li>
                        <li><strong>How We Collect It:</strong> Only when you actively use the app's camera or map features.</li>
                        <li><strong>Purpose:</strong> To overlay accurate geographic data on your photos and display your position on the mini-map within the app.</li>
                        <li><strong>Storage:</strong> All location data is stored exclusively on your device. We do not transmit, upload, or store location data on external servers.</li>
                    </ul>

                    <h3>1.2 Camera Access</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> Access to your device's camera to capture photos.</li>
                        <li><strong>Purpose:</strong> To enable the core functionality of taking geo-tagged photographs.</li>
                        <li><strong>Storage:</strong> Photos are saved locally to your device's gallery or the app's dedicated album (Pictures/GEOCAM PRO).</li>
                    </ul>

                    <h3>1.3 Photo Metadata (EXIF Data)</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> We embed GPS coordinates, altitude, timestamp, and other metadata into the EXIF data of photos you capture.</li>
                        <li><strong>Purpose:</strong> To provide professional geo-tagged photos with accurate location information.</li>
                        <li><strong>User Control:</strong> You can disable location tagging in the app settings. Metadata remains on your device and is only shared if you choose to export or share the photo.</li>
                    </ul>

                    <h3>1.4 Storage/Photos Access</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> Permission to read and write photos to your device's storage.</li>
                        <li><strong>Purpose:</strong> To save captured photos to your gallery and allow you to view/edit previously taken photos within the app.</li>
                        <li><strong>Storage:</strong> All photos remain on your device. We do not access, upload, or transmit your photos to any external servers.</li>
                    </ul>

                    <h3>1.5 Device Information</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> Device model, operating system version, app version, and crash logs (if the app crashes).</li>
                        <li><strong>Purpose:</strong> To improve app performance, fix bugs, and ensure compatibility across devices.</li>
                        <li><strong>Third-Party Services:</strong> Crash logs may be collected via Google Play Services for diagnostic purposes.</li>
                    </ul>

                    <h3>1.6 Weather Data</h3>
                    <ul>
                        <li><strong>What We Collect:</strong> Your current GPS coordinates are sent to the OpenWeather API to retrieve local weather information (temperature, conditions).</li>
                        <li><strong>Purpose:</strong> To display weather data on your photo overlays.</li>
                        <li><strong>Data Sharing:</strong> Only your coordinates are shared with OpenWeather. No personal identifiers (name, email, device ID) are transmitted.</li>
                        <li><strong>Third-Party Policy:</strong> <a href="https://openweathermap.org/privacy-policy" target="_blank" rel="noopener noreferrer">OpenWeather Privacy Policy</a></li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Eye size={24} />
                    </div>
                    <h2>2. How We Use Your Information</h2>
                    <p>We use the collected information solely for the following purposes:</p>
                    <ul>
                        <li><strong>Core Functionality:</strong> To capture, display, and save geo-tagged photos with accurate GPS data.</li>
                        <li><strong>Map Display:</strong> To show your current location on the in-app mini-map and watermark overlays.</li>
                        <li><strong>Weather Overlay:</strong> To fetch and display local weather information on your photos.</li>
                        <li><strong>App Improvement:</strong> To analyze crash logs and performance metrics to improve stability and user experience.</li>
                    </ul>

                    <div className="highlight-box">
                        <h3>We do NOT:</h3>
                        <ul>
                            <li>Sell, rent, or share your personal data with third parties for marketing purposes</li>
                            <li>Upload your photos or location history to cloud servers</li>
                            <li>Track your location when the app is not in use</li>
                        </ul>
                    </div>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <ShieldCheck size={24} />
                    </div>
                    <h2>3. Data Storage and Security</h2>

                    <h3>3.1 Local Storage</h3>
                    <ul>
                        <li>All photos, GPS data, and app settings are stored exclusively on your device.</li>
                        <li>We do not have access to your data. We do not operate cloud servers or databases.</li>
                    </ul>

                    <h3>3.2 Data Security</h3>
                    <ul>
                        <li>Photos and metadata are protected by your device's built-in security features (encryption, lock screen, etc.).</li>
                        <li>We implement industry-standard security practices within the app to prevent unauthorized access.</li>
                    </ul>

                    <h3>3.3 Data Retention</h3>
                    <ul>
                        <li>Data is retained on your device until you manually delete it (via the app's gallery, device storage settings, or uninstalling the app).</li>
                        <li>Uninstalling the app will remove app-specific data, but photos saved to your device's public gallery (Pictures/GEOCAM PRO) will remain unless manually deleted.</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Globe size={24} />
                    </div>
                    <h2>4. Third-Party Services</h2>
                    <p>We integrate the following third-party services:</p>

                    <h3>4.1 Map Tiles</h3>
                    <ul>
                        <li><strong>Provider:</strong> OpenStreetMap / Mapbox</li>
                        <li><strong>Data Shared:</strong> Map tile requests based on your current location (coordinates only).</li>
                        <li><strong>Purpose:</strong> To display map backgrounds in the app's mini-map and watermark overlays.</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">OpenStreetMap Privacy</a> | <a href="https://www.mapbox.com/legal/privacy" target="_blank" rel="noopener noreferrer">Mapbox Privacy</a></li>
                    </ul>

                    <h3>4.2 Weather API</h3>
                    <ul>
                        <li><strong>Provider:</strong> OpenWeather</li>
                        <li><strong>Data Shared:</strong> Your GPS coordinates to fetch local weather data.</li>
                        <li><strong>Purpose:</strong> To display temperature and weather conditions on photo overlays.</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://openweathermap.org/privacy-policy" target="_blank" rel="noopener noreferrer">OpenWeather Privacy Policy</a></li>
                    </ul>

                    <h3>4.3 Google AdMob (Advertising)</h3>
                    <ul>
                        <li><strong>Provider:</strong> Google AdMob</li>
                        <li><strong>Data Shared:</strong> Device advertising ID, app usage data, and approximate location (for ad targeting).</li>
                        <li><strong>Purpose:</strong> To display banner and interstitial ads (free version only).</li>
                        <li><strong>User Control:</strong> You can opt out of personalized ads via your device's privacy settings (Android: Settings &gt; Google &gt; Ads &gt; Opt out of Ads Personalization).</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google AdMob Privacy</a></li>
                    </ul>

                    <h3>4.4 Google Play Billing</h3>
                    <ul>
                        <li><strong>Provider:</strong> Google Play Store</li>
                        <li><strong>Data Shared:</strong> Purchase information for premium subscriptions.</li>
                        <li><strong>Purpose:</strong> To process in-app purchases and manage premium features.</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Play Privacy</a></li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Lock size={24} />
                    </div>
                    <h2>5. Your Rights (GDPR & CCPA Compliance)</h2>
                    <p>We respect your privacy rights under international regulations, including GDPR (EU) and CCPA (California, USA).</p>

                    <h3>5.1 Right to Access</h3>
                    <p>You have the right to access all personal data stored by the app. Since all data is stored locally on your device, you can access it directly via the app's gallery and settings.</p>

                    <h3>5.2 Right to Deletion</h3>
                    <p>You can delete your data at any time by:</p>
                    <ul>
                        <li>Deleting individual photos from the app's gallery or device storage</li>
                        <li>Clearing app data via your device settings (Settings &gt; Apps &gt; GEOCAM PRO &gt; Storage &gt; Clear Data)</li>
                        <li>Uninstalling the app</li>
                    </ul>

                    <h3>5.3 Right to Opt-Out</h3>
                    <ul>
                        <li><strong>Location Services:</strong> You can disable location access at any time via your device settings (Settings &gt; Apps &gt; GEOCAM PRO &gt; Permissions &gt; Location &gt; Deny).</li>
                        <li><strong>Personalized Ads:</strong> You can opt out of personalized advertising via your device's ad settings.</li>
                    </ul>

                    <h3>5.4 Right to Portability</h3>
                    <p>You can export your photos and metadata at any time by sharing them via email, cloud storage, or other apps.</p>

                    <h3>5.5 Right to Withdraw Consent</h3>
                    <p>You can withdraw consent for any permission (camera, location, storage) at any time via your device settings. Note that withdrawing essential permissions may limit app functionality.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Users size={24} />
                    </div>
                    <h2>6. Children's Privacy</h2>
                    <p>
                        GEOCAM PRO is not intended for use by children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us, and we will take steps to delete such information.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>7. International Data Transfers</h2>
                    <p>
                        Since all data is stored locally on your device, there are no international data transfers. The only external data transmissions are:
                    </p>
                    <ul>
                        <li>GPS coordinates to OpenWeather API (for weather data)</li>
                        <li>Map tile requests to OpenStreetMap/Mapbox (for map display)</li>
                        <li>Advertising data to Google AdMob (for ad serving)</li>
                    </ul>
                    <p>
                        These services may process data in countries outside your region. Please review their respective privacy policies for details.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Bell size={24} />
                    </div>
                    <h2>8. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by:
                    </p>
                    <ul>
                        <li>Displaying a notice within the app</li>
                        <li>Updating the "Last Updated" date at the top of this policy</li>
                    </ul>
                    <p>
                        Your continued use of the app after changes are posted constitutes your acceptance of the updated policy.
                    </p>
                </section>

                <section className="terms-section contact-section">
                    <div className="section-icon">
                        <FileText size={24} />
                    </div>
                    <h2>9. Contact Us</h2>
                    <p>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> nishantharadhya4@gmail.com</p>
                        <p><strong>Website:</strong> <a href="https://www.nexaaradhya.site/" target="_blank" rel="noopener noreferrer">https://www.nexaaradhya.site/</a></p>
                        <p><strong>Developer:</strong> Nishanth Aradhya AG</p>
                    </div>
                </section>

                <section className="terms-section">
                    <h2>10. Governing Law</h2>
                    <p>
                        This Privacy Policy is governed by the laws of India, without regard to its conflict of law provisions.
                    </p>
                    <p>
                        By using GEOCAM PRO, you acknowledge that you have read, understood, and agree to this Privacy Policy.
                    </p>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2026 GEOCAM PRO. All rights reserved.</p>
            </div>
        </div>
    );
};

// Detailed Ats.Ai Privacy Policy Component
const ATSAPrivacy = ({ navigate }) => {
    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>🤖 Ats.Ai - Privacy Policy</h1>
                    <p className="last-updated">Effective date: May 10, 2026</p>
                </div>
            </div>

            <div className="terms-content">
                <p>This Privacy Policy explains how ATS Resume Builder, operated by Nishanth Aradhya ("we," "us," or "our"), collects, uses, shares, stores, and deletes information when you use the ATS Resume Builder mobile app, website, backend services, and related features (together, the "Service").</p>
                <p>Privacy contact: <a href="mailto:nishantharadhya7@gmail.com">nishantharadhya7@gmail.com</a></p>

                <section className="terms-section">
                    <div className="section-icon"><Database size={24} /></div>
                    <h2>1. Information We Collect</h2>
                    <p>We collect information you provide, information created while using the Service, and information collected automatically by our app and service providers.</p>
                    
                    <h3>Account information</h3>
                    <ul>
                        <li>Name and Email address</li>
                        <li>Firebase user ID and authentication tokens</li>
                        <li>Sign-in provider information (including Google Sign-In)</li>
                        <li>Subscription status and account metadata</li>
                    </ul>

                    <h3>Resume, profile, and job-search content</h3>
                    <p>Depending on the features you use, we may collect and store:</p>
                    <ul>
                        <li>Resume details (name, contact info, experience, education, skills, etc.)</li>
                        <li>Cover letters and generated text</li>
                        <li>Job tracker information (companies, roles, status)</li>
                        <li>Profile photos and generated PDF/DOCX files</li>
                        <li>LinkedIn profile data if you choose to import it</li>
                    </ul>

                    <h3>Files, photos, and device permissions</h3>
                    <p>The app may request permission to access photos or files for features like profile photo uploads or LinkedIn ZIP imports. The app does not access your files in the background.</p>

                    <h3>AI feature inputs and outputs</h3>
                    <p>When you use AI features (ATS analysis, tailoring, etc.), we process the text you submit and the AI-generated output.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Lock size={24} /></div>
                    <h2>2. How We Use Information</h2>
                    <ul>
                        <li>Create, authenticate, and manage your account</li>
                        <li>Save, sync, display, edit, export, and delete career documents</li>
                        <li>Generate ATS scores and AI-powered content</li>
                        <li>Provide free, ad-supported, and paid features</li>
                        <li>Monitor reliability, fix bugs, and maintain security</li>
                        <li>Comply with applicable laws and Google Play policies</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><CreditCard size={24} /></div>
                    <h2>3. Subscriptions and In-App Purchases</h2>
                    <p>ATS.ai offers optional paid subscriptions (Monthly, Yearly, and Lifetime) that unlock Pro features. All payments are securely processed by Google Play. We do not collect, store, or have access to your payment card information.</p>
                    <p>Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period. You can manage or cancel your subscription anytime through your Google Play account settings.</p>
                    <p>For refund requests, please contact Google Play support as all billing is handled by them.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><AlertCircle size={24} /></div>
                    <h2>4. AI Processing and Content Responsibility</h2>
                    <p>The Service uses AI to assist with document writing. AI outputs may be inaccurate or inappropriate. You are responsible for reviewing, editing, and verifying all AI-generated content before using it in job applications.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Users size={24} /></div>
                    <h2>5. How We Share Information</h2>
                    <p>We share information with service providers including: Google Firebase, Google Mobile Ads (AdMob), Google Play Billing, RevenueCat, and AI providers (Google Gemini, Groq).</p>
                    <p>If you choose to export or share a resume, the information is shared with the apps or services you select.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><ShieldCheck size={24} /></div>
                    <h2>6. Data Security</h2>
                    <p>We use reasonable technical measures to protect information, including HTTPS, Firebase security rules, and authentication. No method of storage or transmission is 100% secure.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Wifi size={24} /></div>
                    <h2>7. Data Retention</h2>
                    <p>We retain information as long as needed to provide the Service. Account records and resume data are kept until you delete them or request account deletion.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><AlertCircle size={24} /></div>
                    <h2>8. Deleting Your Information and Account</h2>
                    <p>To request deletion of your account and associated app data, use the account deletion page at: <a href="/delete-account/atsai">https://www.nexaaradhya.site/delete-account/atsai</a></p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Globe size={24} /></div>
                    <h2>9. Your Choices</h2>
                    <p>You can choose whether to create an account, manage ad personalization, and decline optional permissions. You can also request access, correction, or deletion by contacting us.</p>
                </section>

                <section className="terms-section">
                    <h2>10. Children's Privacy</h2>
                    <p>ATS Resume Builder is intended for professional use and is not directed to children under 13. We do not knowingly collect personal information from children under 13.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Globe size={24} /></div>
                    <h2>11. International Users</h2>
                    <p>Your information may be processed in countries other than where you live, which may have different data protection laws.</p>
                </section>

                <section className="terms-section contact-section">
                    <div className="section-icon"><Mail size={24} /></div>
                    <h2>Contact Us</h2>
                    <p>For privacy questions, contact:</p>
                    <div className="contact-info">
                        <p><strong>Nishanth Aradhya</strong></p>
                        <p>ATS Resume Builder</p>
                        <p>Email: nishantharadhya7@gmail.com</p>
                    </div>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2026 Ats.Ai. All rights reserved.</p>
            </div>
        </div>
    );
};

// Detailed OurVerse Privacy Policy Component
const OurVersePrivacy = ({ navigate }) => {
    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>🌌 OurVerse - Privacy Policy</h1>
                    <p className="last-updated">Effective Date: August 24, 2026</p>
                </div>
            </div>

            <div className="terms-content">
                <p>OurVerse ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by our mobile application (the "App").</p>
                <p>By downloading and using the App, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

                <section className="terms-section">
                    <div className="section-icon"><Database size={24} /></div>
                    <h2>1. Information We Collect</h2>
                    <h3>A. Information You Provide to Us</h3>
                    <p><strong>User-Generated Content:</strong> To provide the core features of the App (such as "Doodle with Me", "Flash", "Would You Rather", and chat), we collect the text, images, drawings, and interactions you create and share with your partner or group space members.</p>
                    <p><strong>Account Data:</strong> If you use phone, email, or anonymous authentication, we collect identifiers required to create and secure your "Space".</p>
                    <p><strong>Multi-Member (Group) Spaces:</strong> OurVerse allows you to create Spaces with more than two people (e.g., friend groups or family). Content shared in a Group Space (such as chats, Flash photos, and status updates) is visible to all active members of that specific Space. By inviting members or joining a Group Space, you consent to sharing your user-generated content with all other members of that Space.</p>
                    
                    <h3>B. Information Collected Automatically</h3>
                    <p><strong>Device Information:</strong> We collect device-specific information (such as hardware model, operating system version, unique device identifiers, and mobile network information) to ensure the App functions correctly.</p>
                    <p><strong>Usage Data:</strong> We collect diagnostic and performance data, including crash logs and app interaction metrics.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Shield size={24} /></div>
                    <h2>2. Device Permissions</h2>
                    <p>The App requires certain device permissions to function. We only access these permissions when you use the relevant feature:</p>
                    <ul>
                        <li><strong>Camera:</strong> Required for the "Flash" mini-game to capture photos.</li>
                        <li><strong>Microphone:</strong> Required for "Voice Calling" and "Streaming" features to transmit audio.</li>
                        <li><strong>Photo Library & Storage:</strong> Required to select and upload existing photos for games, and to pick local audio files from your device for the "Vibe Together" synced music feature.</li>
                        <li><strong>Notifications & Alarms:</strong> Required to send you updates, daily reminders, and incoming call alerts (including full-screen lock screen intents).</li>
                        <li><strong>Bluetooth:</strong> Required to properly route audio to your Bluetooth headsets during voice calls.</li>
                        <li><strong>Background & Foreground Services:</strong> We utilize foreground services to keep media playback, voice calls, and screen streaming active while the app is in the background.</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Lock size={24} /></div>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use the collected information for various purposes:</p>
                    <ul>
                        <li>To provide, maintain, and improve the App's real-time synchronization features.</li>
                        <li>To notify you about changes to our App or partner activities.</li>
                        <li>To facilitate multi-member group communication and data synchronization.</li>
                        <li>To provide customer support.</li>
                        <li>To detect, prevent, and address technical issues.</li>
                        <li>To serve personalized advertisements (via Google AdMob).</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Globe size={24} /></div>
                    <h2>4. Third-Party Services</h2>
                    <p>The App utilizes third-party services that may collect information used to identify you. By using the App, you also agree to the privacy policies of these third-party service providers:</p>
                    <ul>
                        <li><strong>Google Play Services:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
                        <li><strong>Firebase & Google Analytics for Firebase:</strong> Used for database storage, real-time syncing, authentication, and crash reporting. <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a></li>
                        <li><strong>Vercel:</strong> Our custom backend APIs are hosted on Vercel. We transmit temporary access tokens and notification requests through these servers. <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Vercel Privacy Policy</a></li>
                        <li><strong>Agora:</strong> The Voice Calling and Streaming features utilize the Agora SDK for real-time communication. Audio data is processed by Agora's servers to facilitate the connection. <a href="https://www.agora.io/en/privacy-policy" target="_blank" rel="noopener noreferrer">Agora Privacy Policy</a></li>
                        <li><strong>Google AdMob:</strong> Used to serve interstitial advertisements. AdMob may use device identifiers and cookies to personalize ads. <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google AdMob Policy</a></li>
                        <li><strong>YouTube API Services:</strong> The "Watch Together" feature uses YouTube API Services to sync videos. By using this feature, you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a> and acknowledge the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><AlertCircle size={24} /></div>
                    <h2>5. Data Retention and Deletion</h2>
                    <p>We retain your data only for as long as is necessary for the purposes set out in this Privacy Policy.</p>
                    <p><strong>Ephemeral Data:</strong> Certain features like the "Flash" photos are designed to be ephemeral. Flash photos are permanently and physically deleted from our servers 24 hours after they are uploaded. <strong>Data Deletion:</strong> You have the right to request the deletion of your data. You can delete your "Space" directly within the App settings, which will permanently remove your user-generated content, chat logs, and associated media from our active servers for all members of that space.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><ShieldCheck size={24} /></div>
                    <h2>6. Security of Data</h2>
                    <p>The security of your data is important to us. We use industry-standard encryption (such as HTTPS/TLS) to transmit data between your device and our backend services (Firebase). However, remember that no method of transmission over the internet or method of electronic storage is 100% secure.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Users size={24} /></div>
                    <h2>7. Children's Privacy</h2>
                    <p>Our App does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us. If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon"><Eye size={24} /></div>
                    <h2>8. Changes to This Privacy Policy</h2>
                    <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. You are advised to review this Privacy Policy periodically for any changes.</p>
                </section>

                <section className="terms-section contact-section">
                    <div className="section-icon"><Mail size={24} /></div>
                    <h2>9. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> nishantharadhya0@gmail.com</p>
                    </div>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2026 OurVerse. All rights reserved.</p>
            </div>
        </div>
    );
};

const AppPrivacy = () => {
    const { appId } = useParams();
    const navigate = useNavigate();
    const app = appData[appId];

    // Use detailed NearTransfer privacy policy
    if (appId === 'neartransfer') {
        return <NearTransferPrivacy navigate={navigate} />;
    }

    // Use detailed PDFGen privacy policy
    if (appId === 'pdfgen') {
        return <PDFGenPrivacy navigate={navigate} />;
    }

    // Use detailed Geocam privacy policy
    if (appId === 'geocam') {
        return <GeocamPrivacy navigate={navigate} />;
    }

    // Use detailed ATSAI privacy policy
    if (appId === 'atsai') {
        return <ATSAPrivacy navigate={navigate} />;
    }

    // Use detailed OurVerse privacy policy
    if (appId === 'ourverse') {
        return <OurVersePrivacy navigate={navigate} />;
    }

    if (!app) {
        return (
            <div className="terms-container" data-theme="dark">
                <div className="terms-header">
                    <button className="back-button" onClick={() => navigate('/')}>
                        <ArrowLeft size={20} />
                        <span>Back to Home</span>
                    </button>
                    <div className="terms-title-section">
                        <AlertCircle className="terms-icon" size={40} />
                        <h1>App Not Found</h1>
                    </div>
                </div>
                <div className="terms-content">
                    <p>The requested app was not found. Please check the URL.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="terms-container" data-theme="dark">
            <div className="terms-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </button>
                <div className="terms-title-section">
                    <Shield className="terms-icon" size={40} />
                    <h1>{app.icon} {app.name} - Privacy Policy</h1>
                    <p className="last-updated">Last Updated: December 2024</p>
                </div>
            </div>

            <div className="terms-content">
                <section className="terms-section">
                    <div className="section-icon">
                        <Eye size={24} />
                    </div>
                    <h2>1. Introduction</h2>
                    <p>
                        NishanthAradhya ("we," "our," or "us") is committed to protecting your privacy.
                        This Privacy Policy explains how {app.name} handles your information.
                        {app.name} is a {app.description}.
                    </p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Database size={24} />
                    </div>
                    <h2>2. Information We Collect</h2>
                    <p>{app.name} is designed with privacy as a priority:</p>
                    <ul>
                        {app.dataCollection.map((item, idx) => (
                            <li key={idx}>{item}</li>
                        ))}
                    </ul>
                    <p><strong>Anonymous Data:</strong> We may collect anonymous crash reports and usage statistics to improve app performance.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Lock size={24} />
                    </div>
                    <h2>3. App Permissions</h2>
                    <p>{app.name} requires the following permissions:</p>
                    <ul>
                        {app.permissions.map((perm, idx) => (
                            <li key={idx}>{perm}</li>
                        ))}
                    </ul>
                    <p>These permissions are used solely for app functionality and not for data collection.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Users size={24} />
                    </div>
                    <h2>4. Third-Party Services</h2>
                    <p>{app.name} may use these third-party services:</p>
                    <ul>
                        <li><strong>Google AdMob:</strong> For displaying advertisements (collects anonymous ad identifiers)</li>
                        <li><strong>Google Play Services:</strong> For app updates and in-app purchases</li>
                        <li><strong>Firebase Crashlytics:</strong> For anonymous crash reporting</li>
                    </ul>
                    <p>These services have their own privacy policies.</p>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Bell size={24} />
                    </div>
                    <h2>5. Your Rights</h2>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Opt-out of personalized ads in your device settings</li>
                        <li>Request deletion of any analytics data</li>
                        <li>Uninstall the app at any time (removes all local data)</li>
                        <li>Contact us with privacy concerns</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <div className="section-icon">
                        <Globe size={24} />
                    </div>
                    <h2>6. Children's Privacy</h2>
                    <p>
                        {app.name} is not directed to children under 13. We do not knowingly collect
                        personal information from children. If you believe a child has provided us with
                        personal information, please contact us.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>7. Data Security</h2>
                    <p>
                        Since {app.name} processes data locally on your device, your files and data
                        remain under your control. We implement industry-standard security practices
                        for any analytics data we collect.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>8. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of any
                        changes by updating the "Last Updated" date at the top of this page.
                    </p>
                </section>

                <section className="terms-section contact-section">
                    <h2>9. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> aradhyanishanth84@gmail.com</p>
                        <p><strong>Address:</strong> Mysore, Karnataka, India</p>
                    </div>
                </section>
            </div>

            <div className="terms-footer">
                <p>© 2024 NishanthAradhya. All Rights Reserved.</p>
            </div>
        </div>
    );
};

export default AppPrivacy;
